import { useEffect, useState } from 'preact/hooks';
import {
  platformOrgs,
  platformOps,
  OrgDetail,
  Member,
  EventNodeRow,
  StorageBreakdown,
  GatewayConnection,
  PipelineHealth,
  NotificationSummary,
  OffboardingSchedule,
  FeatureFlag,
  platformNotifier,
  formatBytes,
  formatInt,
} from './adminApi';
import { platformAudit, AuditRow, platformAuth } from './api';
import { StepUpModal, StepUpRequiredError, runWithStepUp } from './StepUpModal';
import { OrgBillingTab } from './OrgBillingTab';

type Tab = 'overview' | 'actions' | 'billing' | 'events' | 'members' | 'gateway' | 'storage' | 'notifications' | 'audit' | 'pipeline';

interface Props {
  orgId: string;
  onBack(): void;
}

export function OrgDetailPage({ orgId, onBack }: Props) {
  const [tab, setTab] = useState<Tab>('overview');
  const [org, setOrg] = useState<OrgDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    platformOrgs
      .get(orgId)
      .then((r) => setOrg(r.org))
      .catch((e) => setErr(e instanceof Error ? e.message : 'Unknown error'));
  }, [orgId]);

  if (err) return <div class="error-banner">{err}</div>;
  if (!org) return <p>Loading…</p>;

  const tabs: Array<{ key: Tab; label: string }> = [
    { key: 'overview', label: 'Overview' },
    { key: 'billing', label: 'Billing' },
    { key: 'actions', label: 'Actions' },
    { key: 'events', label: 'Events' },
    { key: 'members', label: 'Members' },
    { key: 'gateway', label: 'Gateway' },
    { key: 'storage', label: 'Storage' },
    { key: 'notifications', label: 'Notifications' },
    { key: 'audit', label: 'Audit' },
    { key: 'pipeline', label: 'Pipeline' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginBottom: 4 }}>
        <button onClick={onBack}>← Orgs</button>
        <h1 style={{ margin: 0 }}>{org.name}</h1>
        <span class="mono" style={{ color: 'var(--admin-text-dim)' }}>
          /{org.slug} · {org.shortId}
        </span>
        <span
          class={`badge ${org.subscriptionStatus === 'active' ? 'success' : 'failure'}`}
          style={{ marginLeft: 'auto' }}
        >
          {org.subscriptionTier} · {org.subscriptionStatus}
        </span>
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--admin-border)', marginBottom: 16 }}>
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              background: 'transparent',
              border: 'none',
              borderBottom: tab === t.key ? '2px solid var(--admin-accent)' : '2px solid transparent',
              borderRadius: 0,
              padding: '8px 16px',
              color: tab === t.key ? 'var(--admin-text)' : 'var(--admin-text-dim)',
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab org={org} />}
      {tab === 'billing' && <OrgBillingTab orgId={orgId} />}
      {tab === 'actions' && (
        <ActionsTab
          org={org}
          onOrgRefresh={() => platformOrgs.get(orgId).then((r) => setOrg(r.org))}
        />
      )}
      {tab === 'events' && <EventsTab orgId={orgId} />}
      {tab === 'members' && <MembersTab orgId={orgId} />}
      {tab === 'gateway' && <GatewayTab orgId={orgId} />}
      {tab === 'storage' && <StorageTab orgId={orgId} />}
      {tab === 'notifications' && <NotificationsTab orgId={orgId} />}
      {tab === 'audit' && <AuditTab orgId={orgId} />}
      {tab === 'pipeline' && <PipelineTab orgId={orgId} />}
    </div>
  );
}

// ── Overview ──

function OverviewTab({ org }: { org: OrgDetail }) {
  const cards: Array<{ label: string; value: string }> = [
    { label: 'Members', value: formatInt(org.memberCount) },
    { label: 'Events', value: formatInt(org.eventCount) },
    { label: 'Media', value: formatInt(org.mediaCount) },
    { label: 'Storage', value: formatBytes(org.storageBytes) },
    { label: 'Orders (30d)', value: formatInt(org.ordersCount30d) },
    { label: 'Open payment events', value: formatInt(org.openPaymentEvents) },
    { label: 'Stuck pipeline items', value: formatInt(org.stuckPipelineCount) },
  ];

  return (
    <div>
      <section style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
        {cards.map((c) => (
          <div
            key={c.label}
            style={{
              background: 'var(--admin-surface)',
              border: '1px solid var(--admin-border)',
              borderRadius: 8,
              padding: 16,
            }}
          >
            <div style={{ color: 'var(--admin-text-dim)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {c.label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>{c.value}</div>
          </div>
        ))}
      </section>

      <section style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '8px 24px' }}>
        <dt style={{ color: 'var(--admin-text-dim)' }}>Owner</dt>
        <dd class="mono" style={{ margin: 0 }}>{org.ownerName} &lt;{org.ownerEmail}&gt;</dd>
        <dt style={{ color: 'var(--admin-text-dim)' }}>Stripe customer</dt>
        <dd class="mono" style={{ margin: 0 }}>{org.stripeCustomerId || '—'}</dd>
        <dt style={{ color: 'var(--admin-text-dim)' }}>Created</dt>
        <dd class="mono" style={{ margin: 0 }}>{new Date(org.createdAt).toLocaleString()}</dd>
        <dt style={{ color: 'var(--admin-text-dim)' }}>Updated</dt>
        <dd class="mono" style={{ margin: 0 }}>{new Date(org.updatedAt).toLocaleString()}</dd>
      </section>

      <details style={{ marginTop: 24 }}>
        <summary style={{ cursor: 'pointer', color: 'var(--admin-text-dim)' }}>Branding JSON</summary>
        <pre>{prettyJSON(org.brandingJson)}</pre>
      </details>
      <details>
        <summary style={{ cursor: 'pointer', color: 'var(--admin-text-dim)' }}>Settings JSON</summary>
        <pre>{prettyJSON(org.settingsJson)}</pre>
      </details>
      <details>
        <summary style={{ cursor: 'pointer', color: 'var(--admin-text-dim)' }}>Payment settings JSON</summary>
        <pre>{prettyJSON(org.paymentSettingsJson)}</pre>
      </details>
    </div>
  );
}

// ── Actions (Slice 4a) ──

function ActionsTab({ org, onOrgRefresh }: { org: OrgDetail; onOrgRefresh(): void }) {
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [schedule, setSchedule] = useState<OffboardingSchedule | null | undefined>(undefined);
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [stepUpFor, setStepUpFor] = useState<(() => void) | null>(null);
  const [confirm, setConfirm] = useState<{
    title: string;
    body: string;
    onConfirm(reason: string): void;
  } | null>(null);

  const suspended = !!(org.subscriptionStatus === 'suspended' || (org as any).suspendedAt);

  async function load() {
    try {
      const s = await platformOrgs.getOffboarding(org.id);
      setSchedule(s.schedule || null);
      const f = await platformOrgs.getFeatureFlags(org.id);
      setFlags(f.flags || []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Unknown error');
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org.id]);

  async function runAction(fn: () => Promise<void>) {
    setErr(null);
    try {
      await runWithStepUp(fn);
      await load();
      onOrgRefresh();
    } catch (e) {
      if (e instanceof StepUpRequiredError) {
        setStepUpFor(() => () => runAction(fn));
        return;
      }
      setErr(e instanceof Error ? e.message : 'Unknown error');
    }
  }

  const activeSchedule =
    schedule && !schedule.cancelledAt && !schedule.executedAt ? schedule : null;

  return (
    <div>
      {err && <div class="error-banner">{err}</div>}
      {info && (
        <div
          style={{
            background: 'rgba(79, 209, 139, 0.15)',
            border: '1px solid var(--admin-success)',
            color: '#c2f2d6',
            padding: 12,
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {info}
        </div>
      )}

      <section style={{ marginBottom: 32 }}>
        <h2>Suspension</h2>
        <p style={{ color: 'var(--admin-text-dim)' }}>
          Suspending an org blocks uploads and consumer gallery viewing; purchased downloads still
          work. Reversible at any time.
        </p>
        {suspended ? (
          <button
            onClick={() =>
              setConfirm({
                title: 'Unsuspend this org?',
                body: 'The org will resume normal service immediately.',
                onConfirm: (reason) => {
                  setConfirm(null);
                  runAction(async () => {
                    await platformOrgs.unsuspend(org.id, reason);
                    setInfo('Org unsuspended.');
                  });
                },
              })
            }
          >
            Unsuspend
          </button>
        ) : (
          <button
            onClick={() =>
              setConfirm({
                title: 'Suspend this org?',
                body: 'Uploads and consumer gallery views will be blocked. Purchased downloads remain available.',
                onConfirm: (reason) => {
                  if (!reason.trim()) {
                    alert('Reason required.');
                    return;
                  }
                  setConfirm(null);
                  runAction(async () => {
                    await platformOrgs.suspend(org.id, reason);
                    setInfo('Org suspended.');
                  });
                },
              })
            }
          >
            Suspend
          </button>
        )}
      </section>

      <section style={{ marginBottom: 32 }}>
        <h2>Offboarding</h2>
        <p style={{ color: 'var(--admin-text-dim)' }}>
          Scheduling an offboard marks this org eligible for data deletion after the grace window.
          Step-up re-auth is required. Execution (at T-0) is handled by a background worker —
          shipping in Slice 4b; for now, scheduling and cancellation are live but nothing actually
          deletes data yet.
        </p>
        {activeSchedule ? (
          <div
            style={{
              background: 'rgba(240, 180, 41, 0.12)',
              border: '1px solid var(--admin-warning)',
              borderRadius: 8,
              padding: 16,
              marginBottom: 12,
            }}
          >
            <div>
              <strong>Scheduled for:</strong>{' '}
              {new Date(activeSchedule.scheduledAt).toLocaleString()}
            </div>
            <div style={{ color: 'var(--admin-text-dim)', fontSize: 13, marginTop: 4 }}>
              By {activeSchedule.scheduledByUsername} · {activeSchedule.reason}
            </div>
            <button
              style={{ marginTop: 12 }}
              onClick={() =>
                setConfirm({
                  title: 'Cancel offboard?',
                  body: 'The org will no longer be scheduled for removal.',
                  onConfirm: (reason) => {
                    setConfirm(null);
                    runAction(async () => {
                      await platformOrgs.cancelOffboard(org.id, reason);
                      setInfo('Offboard cancelled.');
                    });
                  },
                })
              }
            >
              Cancel offboard
            </button>
          </div>
        ) : (
          <button
            onClick={() =>
              setConfirm({
                title: 'Schedule offboard?',
                body: 'After the grace window (default 30 days), this org becomes eligible for data deletion. Reason required. Requires step-up re-auth.',
                onConfirm: (reason) => {
                  if (!reason.trim()) {
                    alert('Reason required.');
                    return;
                  }
                  const daysStr = window.prompt('Grace days before eligibility:', '30');
                  if (daysStr === null) return;
                  const days = parseInt(daysStr, 10) || 30;
                  setConfirm(null);
                  runAction(async () => {
                    await platformOrgs.scheduleOffboard(org.id, reason, days);
                    setInfo(`Offboard scheduled in ${days} days.`);
                  });
                },
              })
            }
          >
            Schedule offboard
          </button>
        )}
      </section>

      <section>
        <h2>Feature flags</h2>
        <p style={{ color: 'var(--admin-text-dim)' }}>
          Per-org overrides. Value is any JSON — booleans, strings, numbers, or objects.
        </p>
        <FeatureFlagsEditor orgId={org.id} flags={flags} onChange={load} onError={setErr} />
      </section>

      {confirm && (
        <ReasonModal
          title={confirm.title}
          body={confirm.body}
          onCancel={() => setConfirm(null)}
          onConfirm={confirm.onConfirm}
        />
      )}

      {stepUpFor && (
        <StepUpModal
          prompt="This action requires password re-entry."
          onSuccess={() => {
            const retry = stepUpFor;
            setStepUpFor(null);
            retry();
          }}
          onCancel={() => setStepUpFor(null)}
        />
      )}
    </div>
  );
}

function FeatureFlagsEditor({
  orgId,
  flags,
  onChange,
  onError,
}: {
  orgId: string;
  flags: FeatureFlag[];
  onChange(): void;
  onError(msg: string): void;
}) {
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('true');
  const [notes, setNotes] = useState('');

  async function save(e: Event) {
    e.preventDefault();
    try {
      await platformOrgs.setFeatureFlag({
        orgId,
        flagKey: newKey,
        valueJson: newValue,
        notes: notes || undefined,
      });
      setNewKey('');
      setNewValue('true');
      setNotes('');
      onChange();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Unknown error');
    }
  }

  async function del(key: string) {
    if (!window.confirm(`Delete flag "${key}"?`)) return;
    try {
      await platformOrgs.deleteFeatureFlag(orgId, key);
      onChange();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Unknown error');
    }
  }

  return (
    <div>
      <form onSubmit={save} style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 160 }}>
          <label style={{ display: 'block', color: 'var(--admin-text-dim)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Key
          </label>
          <input
            required
            placeholder="new_watermark_ui"
            value={newKey}
            onInput={(e) => setNewKey((e.target as HTMLInputElement).value)}
          />
        </div>
        <div style={{ flex: 1, minWidth: 160 }}>
          <label style={{ display: 'block', color: 'var(--admin-text-dim)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Value (JSON)
          </label>
          <input
            required
            placeholder='true or "darkmode" or {"pct":25}'
            value={newValue}
            onInput={(e) => setNewValue((e.target as HTMLInputElement).value)}
          />
        </div>
        <div style={{ flex: 1, minWidth: 160 }}>
          <label style={{ display: 'block', color: 'var(--admin-text-dim)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Notes
          </label>
          <input
            placeholder="Why this was set"
            value={notes}
            onInput={(e) => setNotes((e.target as HTMLInputElement).value)}
          />
        </div>
        <button class="primary" type="submit">Set flag</button>
      </form>

      {flags.length === 0 ? (
        <p style={{ color: 'var(--admin-text-dim)' }}>No flags set on this org.</p>
      ) : (
        <table class="admin-table">
          <thead>
            <tr>
              <th>Key</th>
              <th>Value</th>
              <th>Set by</th>
              <th>Set at</th>
              <th>Expires</th>
              <th>Notes</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {flags.map((f) => (
              <tr key={f.id}>
                <td class="mono">{f.flagKey}</td>
                <td class="mono">{f.valueJson}</td>
                <td class="mono">{f.setByUsername || '—'}</td>
                <td class="mono">{new Date(f.setAt).toLocaleString()}</td>
                <td class="mono">{f.expiresAt ? new Date(f.expiresAt).toLocaleString() : '—'}</td>
                <td>{f.notes || '—'}</td>
                <td>
                  <button onClick={() => del(f.flagKey)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ReasonModal({
  title,
  body,
  onCancel,
  onConfirm,
}: {
  title: string;
  body: string;
  onCancel(): void;
  onConfirm(reason: string): void;
}) {
  const [reason, setReason] = useState('');
  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--admin-surface)',
          border: '1px solid var(--admin-border)',
          borderRadius: 12,
          padding: 24,
          width: '100%',
          maxWidth: 500,
        }}
      >
        <h2 style={{ marginTop: 0 }}>{title}</h2>
        <p style={{ color: 'var(--admin-text-dim)' }}>{body}</p>
        <div class="field">
          <label>Reason</label>
          <input
            autoFocus
            value={reason}
            onInput={(e) => setReason((e.target as HTMLInputElement).value)}
            placeholder="Why this action is being taken"
          />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onCancel}>Cancel</button>
          <button class="primary" onClick={() => onConfirm(reason)}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Events ──

function EventsTab({ orgId }: { orgId: string }) {
  const [rows, setRows] = useState<EventNodeRow[]>([]);
  const [total, setTotal] = useState(0);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    platformOrgs
      .events(orgId, { limit: 100 })
      .then((r) => {
        setRows(r.events || []);
        setTotal(Number(r.total));
      })
      .catch((e) => setErr(e instanceof Error ? e.message : 'Unknown error'));
  }, [orgId]);

  if (err) return <div class="error-banner">{err}</div>;
  return (
    <div>
      <p style={{ color: 'var(--admin-text-dim)' }}>{formatInt(total)} event nodes</p>
      <table class="admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Depth</th>
            <th>Path</th>
            <th style={{ textAlign: 'right' }}>Media</th>
            <th>State</th>
            <th>Published</th>
            <th>Created</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.id}>
              <td>{e.name}</td>
              <td class="mono">{e.nodeType}</td>
              <td style={{ textAlign: 'right' }}>{e.depth}</td>
              <td class="mono">{e.path}</td>
              <td style={{ textAlign: 'right' }}>{formatInt(e.mediaCount)}</td>
              <td class="mono">{e.galleryState}</td>
              <td class="mono">{e.publishedAt ? new Date(e.publishedAt).toLocaleDateString() : '—'}</td>
              <td class="mono">{new Date(e.createdAt).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && <p style={{ color: 'var(--admin-text-dim)' }}>No events.</p>}
    </div>
  );
}

// ── Members ──

// MEMBERSHIP_ROLES is the editable role set for the platform-admin
// override dropdown. Same set the Studio side uses + server validates.
const MEMBERSHIP_ROLES = ['owner', 'admin', 'photographer', 'uploader', 'viewer'];

function MembersTab({ orgId }: { orgId: string }) {
  const [rows, setRows] = useState<Member[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [stepUpFor, setStepUpFor] = useState<(() => void) | null>(null);
  const [busyUserID, setBusyUserID] = useState<string>('');

  function load() {
    setErr(null);
    platformOrgs
      .members(orgId)
      .then((r) => setRows(r.members || []))
      .catch((e) => setErr(e instanceof Error ? e.message : 'Unknown error'));
  }
  useEffect(() => { load(); }, [orgId]);

  async function impersonate(m: Member) {
    if (m.role !== 'owner' && m.role !== 'admin') {
      alert('Only owner and admin roles can be impersonated. Impersonating photographer/editor/viewer accounts is not supported (their studio access is scoped anyway).');
      return;
    }
    const reason = window.prompt(
      `Reason for impersonating ${m.name} (${m.email})? Audit-logged.`,
      '',
    );
    if (!reason || !reason.trim()) return;

    try {
      const resp = await runWithStepUp(() =>
        platformAuth.startImpersonation({
          targetUserId: m.userId,
          targetOrgId: orgId,
          reason,
        }),
      );
      // Full-page navigation to the redeem URL. The returned HTML
      // sets localStorage.mp_token on the mp-api origin and
      // redirects to /studio.
      window.location.href = resp.redeemUrl;
    } catch (e) {
      if (e instanceof StepUpRequiredError) {
        setStepUpFor(() => () => impersonate(m));
        return;
      }
      setErr(e instanceof Error ? e.message : 'Unknown error');
    }
  }

  // changeRole is the platform-admin override path. Bypasses the
  // Studio-side self-protection (caller is platform staff, not in
  // this org) but the server still enforces last-owner safety. Step-
  // up gated. Audit-logged on the server side. See
  // docs/flows/TEAM-MANAGEMENT.md §"Platform-Admin Override".
  async function changeRole(m: Member, newRole: string) {
    if (newRole === m.role) return;
    setBusyUserID(m.userId);
    try {
      await runWithStepUp(() =>
        platformOrgs.updateMembershipRole(orgId, m.userId, newRole),
      );
      load();
    } catch (e) {
      if (e instanceof StepUpRequiredError) {
        setStepUpFor(() => () => changeRole(m, newRole));
        return;
      }
      setErr(e instanceof Error ? e.message : 'Failed to change role');
    } finally {
      setBusyUserID('');
    }
  }

  async function removeMembership(m: Member) {
    if (!confirm(`Remove ${m.name || m.email} from this org? This is an emergency override path — the user will lose all org access immediately. Audit-logged.`)) return;
    setBusyUserID(m.userId);
    try {
      await runWithStepUp(() =>
        platformOrgs.removeMembership(orgId, m.userId),
      );
      load();
    } catch (e) {
      if (e instanceof StepUpRequiredError) {
        setStepUpFor(() => () => removeMembership(m));
        return;
      }
      setErr(e instanceof Error ? e.message : 'Failed to remove');
    } finally {
      setBusyUserID('');
    }
  }

  if (err) return <div class="error-banner">{err}</div>;
  return (
    <>
      <p style={{ color: 'var(--admin-text-dim)' }}>
        <strong>Members management</strong> — change roles or remove members
        directly from the platform side. Use sparingly: this is the emergency
        path for when the org's owner is unreachable or a compliance request
        arrives. The standard path is for org owners to manage their own team
        from Studio. Step-up re-auth + audit logging on every change.
        Last-owner safety (an org must always have ≥1 owner) is enforced on
        the server regardless of caller.
      </p>
      <table class="admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Invited</th>
            <th>Accepted</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m) => (
            <tr key={m.userId}>
              <td>{m.name}</td>
              <td class="mono">{m.email}</td>
              <td>
                <select
                  value={m.role}
                  disabled={busyUserID === m.userId}
                  onChange={(e) => changeRole(m, (e.target as HTMLSelectElement).value)}
                >
                  {MEMBERSHIP_ROLES.map(r => <option value={r}>{r}</option>)}
                </select>
              </td>
              <td class="mono">{new Date(m.invitedAt).toLocaleDateString()}</td>
              <td class="mono">{m.acceptedAt ? new Date(m.acceptedAt).toLocaleDateString() : '—'}</td>
              <td style={{ display: 'flex', gap: 8 }}>
                {(m.role === 'owner' || m.role === 'admin') && (
                  <button onClick={() => impersonate(m)}>Log in as</button>
                )}
                <button
                  onClick={() => removeMembership(m)}
                  disabled={busyUserID === m.userId}
                  style={{ color: '#a00' }}
                >
                  Remove
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {stepUpFor && (
        <StepUpModal
          prompt="Member-management actions are sensitive and require password re-entry."
          onSuccess={() => {
            const retry = stepUpFor;
            setStepUpFor(null);
            retry();
          }}
          onCancel={() => setStepUpFor(null)}
        />
      )}
    </>
  );
}

// ── Gateway ──

function GatewayTab({ orgId }: { orgId: string }) {
  const [rows, setRows] = useState<GatewayConnection[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    platformOrgs
      .gateway(orgId)
      .then((r) => setRows(r.connections || []))
      .catch((e) => setErr(e instanceof Error ? e.message : 'Unknown error'));
  }, [orgId]);

  if (err) return <div class="error-banner">{err}</div>;
  if (rows.length === 0)
    return (
      <p style={{ color: 'var(--admin-text-dim)' }}>
        No gateway credentials connected. This org can't accept consumer payments until they add Stripe (or other) keys via their Studio settings.
      </p>
    );

  return (
    <table class="admin-table">
      <thead>
        <tr>
          <th>Gateway</th>
          <th>Mode</th>
          <th>Active</th>
          <th>Publishable key</th>
          <th>Last verified</th>
          <th>Verify error</th>
          <th>Events 30d</th>
          <th>Connected by</th>
          <th>Connected at</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((g) => (
          <tr key={g.id}>
            <td class="mono">{g.gateway}</td>
            <td class="mono">{g.mode}</td>
            <td>
              <span class={`badge ${g.active ? 'success' : 'failure'}`}>{g.active ? 'active' : 'inactive'}</span>
            </td>
            <td class="mono">{g.publishableKey ? `${g.publishableKey.slice(0, 12)}…` : '—'}</td>
            <td class="mono">{g.lastVerifiedAt ? new Date(g.lastVerifiedAt).toLocaleString() : '—'}</td>
            <td style={{ color: g.verifyError ? 'var(--admin-danger)' : 'inherit' }}>{g.verifyError || '—'}</td>
            <td style={{ textAlign: 'right' }} class="mono">{formatInt(g.events30d)}</td>
            <td class="mono">{g.connectedByUser || '—'}</td>
            <td class="mono">{new Date(g.connectedAt).toLocaleDateString()}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Storage ──

function StorageTab({ orgId }: { orgId: string }) {
  const [data, setData] = useState<StorageBreakdown | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    platformOrgs
      .storage(orgId)
      .then(setData)
      .catch((e) => setErr(e instanceof Error ? e.message : 'Unknown error'));
  }, [orgId]);

  if (err) return <div class="error-banner">{err}</div>;
  if (!data) return <p>Loading…</p>;

  return (
    <div>
      <p style={{ color: 'var(--admin-text-dim)' }}>
        Total: <strong>{formatInt(data.totalMedia)}</strong> media files ·{' '}
        <strong>{formatBytes(data.totalBytes)}</strong> (real bytes — one on-disk copy per media row regardless of purchases or views).
      </p>

      <h3>By storage tier</h3>
      <table class="admin-table">
        <thead>
          <tr>
            <th>Tier</th>
            <th style={{ textAlign: 'right' }}>Media</th>
            <th style={{ textAlign: 'right' }}>Bytes</th>
          </tr>
        </thead>
        <tbody>
          {data.byTier.map((t) => (
            <tr key={t.tier}>
              <td class="mono">{t.tier}</td>
              <td style={{ textAlign: 'right' }}>{formatInt(t.mediaCount)}</td>
              <td style={{ textAlign: 'right' }}>{formatBytes(t.bytes)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 style={{ marginTop: 24 }}>By gallery visibility</h3>
      <table class="admin-table">
        <thead>
          <tr>
            <th>State</th>
            <th style={{ textAlign: 'right' }}>Media</th>
            <th style={{ textAlign: 'right' }}>Bytes</th>
          </tr>
        </thead>
        <tbody>
          {data.byVisibility.map((v) => (
            <tr key={v.galleryState}>
              <td class="mono">{v.galleryState}</td>
              <td style={{ textAlign: 'right' }}>{formatInt(v.mediaCount)}</td>
              <td style={{ textAlign: 'right' }}>{formatBytes(v.bytes)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Notifications ──

function NotificationsTab({ orgId }: { orgId: string }) {
  const [rows, setRows] = useState<NotificationSummary[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => {
    platformNotifier
      .query({ orgId, limit: 100 })
      .then((r) => setRows(r.rows || []))
      .catch((e) => setErr(e instanceof Error ? e.message : 'Unknown error'));
  }, [orgId]);

  if (err) return <div class="error-banner">{err}</div>;
  return (
    <div>
      <p style={{ color: 'var(--admin-text-dim)' }}>
        Every outbound email/SMS the platform has sent on behalf of this org. Click a row to view the full body (audit-logged).
      </p>
      <NotificationTable rows={rows} onOpen={setSelected} />
      {selected && <NotificationDetailModal id={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

// ── Audit ──

function AuditTab({ orgId }: { orgId: string }) {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    platformAudit
      .query({ targetKind: 'org', targetId: orgId, limit: 100 })
      .then((r) => setRows(r.rows || []))
      .catch((e) => setErr(e instanceof Error ? e.message : 'Unknown error'));
  }, [orgId]);

  if (err) return <div class="error-banner">{err}</div>;
  if (rows.length === 0)
    return (
      <p style={{ color: 'var(--admin-text-dim)' }}>
        No audit rows target this org yet. Audit rows appear here as soon as a platform user takes any action against this organization.
      </p>
    );

  return (
    <table class="admin-table">
      <thead>
        <tr>
          <th>When</th>
          <th>Who</th>
          <th>Action</th>
          <th>Outcome</th>
          <th>From</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id}>
            <td class="mono">{new Date(r.occurredAt).toLocaleString()}</td>
            <td>{r.platformUsername}</td>
            <td class="mono">{r.action}</td>
            <td>
              <span class={`badge ${r.success ? 'success' : 'failure'}`}>{r.success ? 'ok' : 'fail'}</span>
            </td>
            <td class="mono">{r.requestIp || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Pipeline ──

function PipelineTab({ orgId }: { orgId: string }) {
  const [data, setData] = useState<PipelineHealth | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function load() {
    try {
      const r = await platformOrgs.pipelineHealth(orgId);
      setData(r);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Unknown error');
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  async function requeue(mediaId: string) {
    const reason = window.prompt('Reason for requeue (audit-captured):', '');
    if (!reason || !reason.trim()) return;
    try {
      await platformOps.requeue(mediaId, reason);
      setInfo(`Requeued ${mediaId.slice(0, 8)}…`);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Unknown error');
    }
  }

  if (err) return <div class="error-banner">{err}</div>;
  if (!data) return <p>Loading…</p>;

  return (
    <div>
      {info && (
        <div
          style={{
            background: 'rgba(79, 209, 139, 0.15)',
            border: '1px solid var(--admin-success)',
            color: '#c2f2d6',
            padding: 12,
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          {info}
        </div>
      )}
      <h3>Media by pipeline state</h3>
      <table class="admin-table" style={{ maxWidth: 400 }}>
        <thead>
          <tr>
            <th>Stage</th>
            <th style={{ textAlign: 'right' }}>Count</th>
          </tr>
        </thead>
        <tbody>
          {data.stageCounts.map((s) => (
            <tr key={s.stage}>
              <td class="mono">{s.stage}</td>
              <td style={{ textAlign: 'right' }}>{formatInt(s.count)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3 style={{ marginTop: 24 }}>
        Stuck media ({data.stuck.length})
      </h3>
      <p style={{ color: 'var(--admin-text-dim)' }}>
        Not 'published' and no update in {Math.round(Number(data.stuckThresholdSeconds) / 60)} minutes.
      </p>
      {data.stuck.length === 0 ? (
        <p style={{ color: 'var(--admin-text-dim)' }}>None stuck — pipeline healthy.</p>
      ) : (
        <table class="admin-table">
          <thead>
            <tr>
              <th>Media ID</th>
              <th>Filename</th>
              <th>State</th>
              <th>Last event</th>
              <th>Event</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {data.stuck.map((s) => (
              <tr key={s.id}>
                <td class="mono">{s.id.slice(0, 8)}…</td>
                <td>{s.filename || '—'}</td>
                <td class="mono">{s.pipelineState}</td>
                <td class="mono">{new Date(s.lastEventAt).toLocaleString()}</td>
                <td>{s.eventName}</td>
                <td>
                  <button onClick={() => requeue(s.id)}>Requeue</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Shared: notifications table + body-view modal ──

export function NotificationTable({ rows, onOpen }: { rows: NotificationSummary[]; onOpen(id: string): void }) {
  return (
    <table class="admin-table">
      <thead>
        <tr>
          <th>When</th>
          <th>Type</th>
          <th>Channel</th>
          <th>Recipient</th>
          <th>Subject / first line</th>
          <th>Status</th>
          <th>Retention</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} onClick={() => onOpen(r.id)} style={{ cursor: 'pointer' }}>
            <td class="mono">{new Date(r.createdAt).toLocaleString()}</td>
            <td class="mono">{r.notificationType}</td>
            <td class="mono">{r.channel}</td>
            <td class="mono">{r.recipientAddress}</td>
            <td style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {r.subject || <span style={{ color: 'var(--admin-text-dim)' }}>(no subject)</span>}
            </td>
            <td>
              <span
                class={`badge ${
                  r.status === 'sent' ? 'success' : r.status === 'failed' ? 'failure' : ''
                }`}
                style={
                  r.status === 'pending'
                    ? { background: 'var(--admin-surface-raised)', color: 'var(--admin-text-dim)' }
                    : undefined
                }
              >
                {r.status}
              </span>
            </td>
            <td class="mono">{r.retentionClass}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function NotificationDetailModal({ id, onClose }: { id: string; onClose(): void }) {
  const [detail, setDetail] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resentInfo, setResentInfo] = useState<string | null>(null);

  useEffect(() => {
    platformNotifier
      .get(id)
      .then((r) => setDetail(r.notification))
      .catch((e) => setErr(e instanceof Error ? e.message : 'Unknown error'));
  }, [id]);

  async function resend() {
    const reason = window.prompt('Reason for resend:', '');
    if (reason === null) return;
    setResending(true);
    setErr(null);
    try {
      const r = await platformNotifier.resend(id, reason);
      setResentInfo(`Resent. New notification id: ${r.resent.id}`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Unknown error');
    } finally {
      setResending(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--admin-surface)',
          border: '1px solid var(--admin-border)',
          borderRadius: 12,
          padding: 24,
          maxWidth: 900,
          width: '100%',
          maxHeight: '85vh',
          overflow: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
          <h2 style={{ margin: 0 }}>Notification</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={resend} disabled={resending || !detail?.summary?.recipientAddress}>
              {resending ? 'Resending…' : 'Resend'}
            </button>
            <button onClick={onClose}>Close</button>
          </div>
        </div>
        {err && <div class="error-banner">{err}</div>}
        {resentInfo && (
          <div
            style={{
              background: 'rgba(79, 209, 139, 0.15)',
              border: '1px solid var(--admin-success)',
              color: '#c2f2d6',
              padding: 12,
              borderRadius: 8,
              marginTop: 12,
            }}
          >
            {resentInfo}
          </div>
        )}
        {detail && (
          <>
            <p class="mono" style={{ color: 'var(--admin-text-dim)' }}>
              {detail.summary.notificationType} · {detail.summary.channel} → {detail.summary.recipientAddress}
            </p>
            <p>
              <strong>Subject:</strong> {detail.summary.subject || '(none)'}
            </p>
            <h3>Body</h3>
            <pre style={{ whiteSpace: 'pre-wrap' }}>{detail.body || detail.renderedBody}</pre>
            {detail.metadataJson && detail.metadataJson !== '{}' && (
              <>
                <h3>Metadata</h3>
                <pre>{prettyJSON(detail.metadataJson)}</pre>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function prettyJSON(s: string): string {
  try {
    return JSON.stringify(JSON.parse(s), null, 2);
  } catch {
    return s;
  }
}
